import React, { useState, useEffect } from 'react';
import { KeyRound } from 'lucide-react';
import * as passwordApi from '../api/password';
import { clearQuery } from '../lib/launchParams';

/**
 * Spend a reset token on a new password.
 *
 * <strong>This is the page the reset email has always promised and never had.</strong>
 * The backend endpoints have existed throughout; nothing in the portal read the token
 * out of the address bar, so the working path was an admin generating a link by hand and
 * passing it on, and the link they passed on landed here on the home page.
 *
 * The token is validated before the form is shown rather than after it is filled in. An
 * expired link is a common and undramatic thing - they last a day - and finding out after
 * choosing and confirming a password reads as though the password was rejected.
 */
function ResetPasswordPage({ token, onDone, onSwitchToLogin }) {
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let live = true;
    passwordApi.validateToken(token)
      .then((result) => { if (live) setValid(Boolean(result && result.valid)); })
      .catch(() => { if (live) setValid(false); })
      .finally(() => { if (live) setChecking(false); });
    return () => { live = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Those two passwords are not the same');
      return;
    }
    setBusy(true);
    try {
      await passwordApi.resetPassword({ token, newPassword: password });
      // The token is spent and is a single-use credential for this account, so it comes
      // out of the address bar before anything else happens - history, screenshots and
      // the back button all keep whatever is left there.
      clearQuery();
      setDone(true);
    } catch (err) {
      setError(err.name === 'ApiError'
        ? (err.body?.message || err.body?.error || 'That link did not work')
        : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className="page-container fade-in">
        <div className="auth-card">
          <p>Checking your link…</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="page-container fade-in">
        <div className="auth-card">
          <KeyRound className="auth-icon" />
          <h1>Password changed</h1>
          <p className="auth-subtitle">You can log in with it now.</p>
          <button className="primary-btn full-width" onClick={onSwitchToLogin}>Log in</button>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="page-container fade-in">
        <div className="auth-card">
          <KeyRound className="auth-icon" />
          <h1>That link has expired</h1>
          {/*
            * No distinction drawn between expired, already used and never real. All
            * three want the same next step, and telling a stranger which one they have
            * found tells them whether the account exists.
            */}
          <p className="auth-subtitle">
            Reset links last 24 hours and work once. Ask for a new one and it will be in
            your inbox shortly.
          </p>
          <button className="primary-btn full-width" onClick={onDone}>Get a new link</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container fade-in">
      <div className="auth-card">
        <KeyRound className="auth-icon" />
        <h1>Choose a new password</h1>
        <p className="auth-subtitle">Make it one you have not used here before.</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={submit} className="auth-form">
          <div className="form-group">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={busy}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm new password</label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              disabled={busy}
            />
          </div>

          <button type="submit" className="primary-btn full-width" disabled={busy}>
            {busy ? 'Saving…' : 'Save new password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
